// src/GlobalBoundaryFallback.tsx
import { Box, Button, Stack, Typography, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import analytics from 'src/analytics';
import { FORMAT_DATE_TIME } from 'src/utils/constants/config';

interface Props {
  error: ExpectedAny;
  componentStack: string;
  eventId?: string;
}

const GlobalBoundaryFallback = ({ error, componentStack, eventId }: Props) => {
  const theme = useTheme();
  useEffect(() => {
    analytics.logEvent('page_crash', {
      error: error,
      errorInfo: componentStack,
      page_title: window.document.title,
      page_location: window.location.href,
      page_path: window.location.pathname,
      sentry_event_id: eventId,
    });

    console.error('Error caught by Sentry:', error);
    console.error('Component Stack:', componentStack);
  }, [error, componentStack, eventId]);

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <Stack
      minHeight={'100vh'}
      bgcolor={theme.palette.background.paper1}
      justifyContent={'center'}
      alignItems={'center'}
    >
      <Stack
        bgcolor={theme.palette.background.paper}
        borderRadius={theme.shape.borderRadius}
        padding="32px 40px"
        maxWidth={520}
        width="100%"
        textAlign="center"
        boxShadow="0 8px 24px rgba(30, 30, 60, 0.08)"
        direction="column"
        spacing={1}
      >
        <Box mb={2} fontSize={32}>
          ⚠️
        </Box>

        <Typography mb={1} variant="h5">
          Something went wrong
        </Typography>

        <Typography variant="body1" color="text.secondary">
          An unexpected issue occurred. Please reload the page to continue.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Event: {eventId} - {dayjs().format(FORMAT_DATE_TIME.DD_MM_YYYY_HH_MM_A)}
        </Typography>

        <Button onClick={() => handleReload()} variant="contained" color="primary" size="small">
          Reload page
        </Button>
      </Stack>
    </Stack>
  );
};

export default GlobalBoundaryFallback;
